/**
 * Сервис для архивирования и разархивирования файлов
 * Использует JSZip и zip.js для работы с ZIP-архивами в браузере
 */

import { encryptFile, decryptFile } from './cryptoService';
import * as zipjs from '@zip.js/zip.js';

// Константы для алгоритмов шифрования
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256; // длина ключа в битах
const SALT_LENGTH = 16; // длина соли в байтах
const IV_LENGTH = 12; // длина вектора инициализации в байтах
const ITERATION_COUNT = 100000; // количество итераций для PBKDF2

/**
 * Архивирует и шифрует массив файлов
 * @param {File[]} files - Массив файлов для архивирования и шифрования
 * @param {string} password - Пароль для шифрования
 * @param {Object} options - Дополнительные опции
 * @param {boolean} options.includeReadme - Включать ли README.txt в архив
 * @param {string} options.readmeText - Дополнительный текст для README.txt
 * @returns {Promise<Object>} - Объект с зашифрованным архивом и метаданными
 */
export const archiveAndEncrypt = async (files, password, options = {}) => {
  try {
    console.log(`Начинаем архивирование и шифрование ${files.length} файлов`);

    if (!files || files.length === 0) {
      throw new Error('Не выбраны файлы для архивирования');
    }

    if (!password || typeof password !== 'string' || password.length < 1) {
      throw new Error('Не указан пароль для шифрования');
    }

    // Проверяем, что все элементы в массиве files являются объектами File
    const validFiles = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file instanceof File) {
        validFiles.push(file);
      } else {
        console.error(`Элемент ${i} не является объектом File:`, file);
        console.warn(`Элемент ${i} будет пропущен`);
      }
    }

    if (validFiles.length === 0) {
      throw new Error('Нет действительных файлов для архивирования');
    }

    if (validFiles.length !== files.length) {
      console.warn(
        `Некоторые элементы не являются файлами. Обрабатываем только ${validFiles.length} из ${files.length}`
      );
    }

    // Используем функцию createPasswordProtectedArchive для создания защищенного паролем архива
    console.log('Создаем защищенный паролем архив...');
    const archiveBlob = await createPasswordProtectedArchive(validFiles, password, options);

    console.log(`ZIP-архив успешно создан (${archiveBlob.size} байт)`);

    // Создаем объект с результатом
    const result = {
      encryptedData: archiveBlob,
      fileList: validFiles.map(file => ({
        name: file.name,
        path: file.webkitRelativePath || file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        lastModified: file.lastModified,
      })),
      encryptionDate: new Date().toISOString(),
    };

    console.log('Архивирование и шифрование успешно завершено');
    return result;
  } catch (error) {
    console.error('Ошибка при архивировании и шифровании:', error);
    throw new Error('Не удалось архивировать и зашифровать файлы: ' + error.message);
  }
};

/**
 * Дешифрует и разархивирует файлы
 * @param {Object} encryptedData - Объект с зашифрованным архивом и метаданными
 * @param {string} password - Пароль для дешифрования
 * @returns {Promise<File[]>} - Массив файлов из архива
 */
export const decryptAndExtract = async (encryptedData, password) => {
  try {
    console.log('Начинаем дешифрование и извлечение файлов');

    // Проверяем, что у нас есть зашифрованные данные
    if (!encryptedData || !encryptedData.encryptedData) {
      throw new Error('Отсутствуют зашифрованные данные');
    }

    // Используем функцию extractPasswordProtectedArchive для извлечения файлов из архива
    console.log('Извлекаем файлы из защищенного паролем архива...');
    const files = await extractPasswordProtectedArchive(encryptedData.encryptedData, password);

    console.log(`Успешно извлечено ${files.length} файлов из архива`);

    // Группируем файлы по каталогам для удобства
    const filesByDirectory = {};
    files.forEach(file => {
      const pathParts = file.name.split('/');
      const isInDirectory = pathParts.length > 1;

      if (isInDirectory) {
        const dirName = pathParts[0];
        if (!filesByDirectory[dirName]) {
          filesByDirectory[dirName] = [];
        }
        filesByDirectory[dirName].push(file);
      } else {
        if (!filesByDirectory['root']) {
          filesByDirectory['root'] = [];
        }
        filesByDirectory['root'].push(file);
      }
    });

    console.log('Структура извлеченных файлов:', filesByDirectory);

    return files;
  } catch (error) {
    console.error('Ошибка при дешифровании и извлечении:', error);
    throw new Error('Не удалось дешифровать и извлечь файлы: ' + error.message);
  }
};

/**
 * Определяет MIME-тип файла по его имени
 * @param {string} fileName - Имя файла
 * @returns {string|null} - MIME-тип или null, если не удалось определить
 */
/**
 * Создает защищенный паролем ZIP-архив из массива файлов
 * @param {File[]} files - Массив файлов для архивирования
 * @param {string} password - Пароль для защиты архива
 * @param {Object} options - Дополнительные опции
 * @param {boolean} options.includeReadme - Включать ли README.txt в архив
 * @param {string} options.readmeText - Дополнительный текст для README.txt
 * @returns {Promise<Blob>} - ZIP-архив в виде Blob
 */
export const createPasswordProtectedArchive = async (files, password, options = {}) => {
  try {
    console.log(`Начинаем создание защищенного паролем архива из ${files.length} файлов`);

    if (!files || files.length === 0) {
      throw new Error('Не выбраны файлы для архивирования');
    }

    if (!password || typeof password !== 'string' || password.length < 1) {
      throw new Error('Не указан пароль для защиты архива');
    }

    // Проверяем, что все элементы в массиве files являются объектами File
    const validFiles = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file instanceof File) {
        validFiles.push(file);
      } else {
        console.error(`Элемент ${i} не является объектом File:`, file);
        console.warn(`Элемент ${i} будет пропущен`);
      }
    }

    if (validFiles.length === 0) {
      throw new Error('Нет действительных файлов для архивирования');
    }

    // Создаем новый ZIP-архив с использованием zip.js
    console.log('Создаем новый ZIP-архив с использованием zip.js...');

    // Создаем BlobWriter для записи архива
    const blobWriter = new zipjs.BlobWriter('application/zip');

    // Создаем ZipWriter с опциями шифрования
    const zipWriter = new zipjs.ZipWriter(blobWriter, {
      // Уровень сжатия (от 0 до 9)
      level: typeof options.compressionLevel === 'number' ? options.compressionLevel : 6,
      // Используем веб-воркеры для ускорения процесса
      useWebWorkers: true,
      bufferedWrite: true,
      keepOrder: true,
    });

    // Проверяем, нужно ли добавлять README.txt
    const includeReadme = options.includeReadme !== undefined ? options.includeReadme : true;

    if (includeReadme) {
      // Формируем содержимое README.txt
      let infoContent = `VaultZip Archive
Created: ${new Date().toISOString()}
Files: ${validFiles.length}
This archive was created with VaultZip - secure file encryption service.
`;

      // Добавляем пользовательский текст, если он есть
      if (options.readmeText) {
        infoContent += `\n${options.readmeText}\n`;
      }

      // Добавляем README.txt с паролем
      const textEncoder = new TextEncoder();
      const readmeBytes = textEncoder.encode(infoContent);
      await zipWriter.add('README.txt', new zipjs.Uint8ArrayReader(readmeBytes), {
        lastModDate: new Date(),
        password: password, // Устанавливаем пароль для файла
        level: typeof options.compressionLevel === 'number' ? options.compressionLevel : undefined,
      });

      console.log('Добавлен информационный файл README.txt');
    }

    // Создаем все необходимые директории
    const directories = new Set();
    for (const file of validFiles) {
      const path = file.webkitRelativePath || file.name;
      const parts = path.split('/');

      if (parts.length > 1) {
        // Это файл в подкаталоге, создаем все промежуточные директории
        let currentPath = '';
        for (let i = 0; i < parts.length - 1; i++) {
          currentPath += parts[i] + '/';
          directories.add(currentPath);
        }
      }
    }

    // Создаем директории
    for (const dir of directories) {
      await zipWriter.add(dir, new zipjs.Uint8ArrayReader(new Uint8Array(0)), {
        directory: true,
        lastModDate: new Date(),
        password: password, // Устанавливаем пароль для директории
      });
      console.log(`Создана директория: ${dir}`);
    }

    // Добавляем файлы в архив
    for (const file of validFiles) {
      try {
        // Определяем путь файла в архиве
        const filePath = file.webkitRelativePath || file.name;
        console.log(`Добавление файла в архив: ${filePath} (${file.size} байт)`);

        // Создаем BlobReader для чтения файла
        const fileReader = new zipjs.BlobReader(file);

        // Добавляем файл в архив с паролем
        await zipWriter.add(filePath, fileReader, {
          lastModDate: new Date(file.lastModified || new Date()),
          password: password, // Устанавливаем пароль для файла
          level: typeof options.compressionLevel === 'number' ? options.compressionLevel : undefined,
        });

        console.log(`Файл ${filePath} успешно добавлен в архив`);
      } catch (fileError) {
        console.error(`Ошибка при добавлении файла ${file.name} в архив:`, fileError);
        throw new Error(`Не удалось добавить файл ${file.name} в архив: ${fileError.message}`);
      }
    }

    // Закрываем архив и получаем Blob
    console.log('Завершаем создание архива...');
    const blob = await zipWriter.close();

    console.log(`Защищенный паролем ZIP-архив успешно создан (${blob.size} байт)`);
    return blob;
  } catch (error) {
    console.error('Ошибка при создании защищенного паролем архива:', error);
    throw new Error('Не удалось создать защищенный паролем архив: ' + error.message);
  }
};

/**
 * Извлекает файлы из защищенного паролем ZIP-архива
 * @param {Blob} archiveBlob - ZIP-архив в виде Blob
 * @param {string} password - Пароль для извлечения архива
 * @returns {Promise<File[]>} - Массив файлов из архива
 */
export const extractPasswordProtectedArchive = async (archiveBlob, password) => {
  try {
    console.log('Начинаем извлечение файлов из защищенного паролем архива');

    if (!archiveBlob) {
      throw new Error('Не указан архив для извлечения');
    }

    if (!password || typeof password !== 'string' || password.length < 1) {
      throw new Error('Не указан пароль для извлечения архива');
    }

    // Загружаем архив с указанием пароля
    console.log('Загружаем архив с паролем...');

    try {
      // Создаем BlobReader для чтения архива
      const blobReader = new zipjs.BlobReader(archiveBlob);

      // Создаем ZipReader с опциями для расшифровки
      const zipReader = new zipjs.ZipReader(blobReader, {
        // Используем веб-воркеры для ускорения процесса
        useWebWorkers: true,
      });

      // Получаем список записей в архиве
      const entries = await zipReader.getEntries();
      console.log(`Архив успешно загружен, найдено ${entries.length} записей`);

      const files = [];

      // Извлекаем каждый файл из архива
      for (const entry of entries) {
        // Пропускаем директории
        if (entry.directory) {
          console.log(`Пропускаем директорию: ${entry.filename}`);
          continue;
        }

        try {
          console.log(`Извлечение файла: ${entry.filename}`);

          // Создаем BlobWriter для записи содержимого файла
          const writer = new zipjs.BlobWriter();

          // Получаем содержимое файла с паролем
          const content = await entry.getData(writer, {
            password: password, // Используем пароль для расшифровки
          });

          // Определяем тип файла по расширению или используем generic
          const fileType = getFileTypeFromName(entry.filename) || 'application/octet-stream';

          // Создаем объект File
          const file = new File([content], entry.filename.split('/').pop(), {
            type: fileType,
            lastModified: entry.lastModDate ? entry.lastModDate.getTime() : new Date().getTime(),
          });

          files.push(file);
          console.log(`Файл ${entry.filename} успешно извлечен из архива`);
        } catch (error) {
          console.error(`Ошибка при извлечении файла ${entry.filename}:`, error);
          // Продолжаем извлечение других файлов
        }
      }

      // Закрываем архив
      await zipReader.close();

      console.log(`Извлечено ${files.length} файлов из архива`);
      return files;
    } catch (error) {
      // Проверяем, связана ли ошибка с неверным паролем
      if (
        error.message &&
        (error.message.includes('Invalid password') ||
          error.message.includes('Bad password') ||
          error.message.includes('encrypted') ||
          error.message.includes('Wrong password'))
      ) {
        throw new Error('Неверный пароль для архива');
      } else {
        throw error;
      }
    }
  } catch (error) {
    console.error('Ошибка при извлечении файлов из защищенного паролем архива:', error);
    throw new Error('Не удалось извлечь файлы из архива: ' + error.message);
  }
};

const getFileTypeFromName = fileName => {
  const extension = fileName.split('.').pop().toLowerCase();
  const mimeTypes = {
    txt: 'text/plain',
    html: 'text/html',
    css: 'text/css',
    js: 'text/javascript',
    json: 'application/json',
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    mp3: 'audio/mpeg',
    mp4: 'video/mp4',
    zip: 'application/zip',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  };

  return mimeTypes[extension] || null;
};
